import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from '@tanstack/react-router'
import { useSubscription } from '@trpc/tanstack-react-query'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useEffect, useId, useState } from 'react'
import type { JsonValue } from 'type-fest'
import type { DropdownChoice } from '@companion-app/shared/Model/Common.js'
import type {
	ClientDevicesListItem,
	ClientSurfaceItem,
	SurfaceGroupConfig,
	SurfacePanelConfig,
} from '@companion-app/shared/Model/Surfaces.js'
import { SimpleDropdownInputField } from '~/Components/DropdownInputFieldSimple'
import { Form, FormLabel } from '~/Components/Form.js'
import { Grid } from '~/Components/Grid'
import { InlineHelpIcon } from '~/Components/InlineHelp'
import { NonIdealState } from '~/Components/NonIdealState'
import { SwitchInputField } from '~/Components/SwitchInputField'
import { TextInputFieldSimple } from '~/Components/TextInputField.js'
import { InternalPageIdDropdown } from '~/Controls/InternalModuleField.js'
import { CloseButton } from '~/Layout/PanelIcons.js'
import { LoadingRetryOrError } from '~/Resources/Loading.js'
import { trpc, useMutationExt } from '~/Resources/TRPC'
import { PreventDefaultHandler, useComputed } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { EditPanelConfigField } from './EditPanelConfigField'

type SurfaceInfo = ClientSurfaceItem & { groupId: string | null }

interface SurfaceEditPanelProps {
	surfaceId: string | null
	groupId: string | null
}

export const SurfaceEditPanel = observer<SurfaceEditPanelProps>(function SurfaceEditPanel({
	surfaceId,
	groupId: rawGroupId,
}) {
	const { surfaces } = useContext(RootAppStoreContext)
	const navigate = useNavigate()

	const doCloseSurface = useCallback(() => {
		void navigate({ to: '/surfaces' })
	}, [navigate])

	let surfaceInfo: SurfaceInfo | null = null
	if (surfaceId) {
		for (const group of surfaces.store.values()) {
			if (surfaceInfo || !group) break

			for (const surface of group.surfaces) {
				if (surface.id === surfaceId) {
					surfaceInfo = {
						...surface,
						groupId: group.isAutoGroup ? null : group.id,
					}
					break
				}
			}
		}
	}

	const groupId = surfaceInfo && !surfaceInfo.groupId ? surfaceId : rawGroupId
	let groupInfo = null
	if (groupId) {
		for (const group of surfaces.store.values()) {
			if (group && group.id === groupId) {
				groupInfo = group
				break
			}
		}
	}

	return (
		<>
			<div className="secondary-panel-simple-header">
				<h4 className="panel-title">
					Settings for {surfaceInfo?.displayName ?? surfaceInfo?.type ?? groupInfo?.displayName}
				</h4>
				<div className="header-buttons">
					<CloseButton closeFn={doCloseSurface} />
				</div>
			</div>

			<div className="secondary-panel-simple-body">
				<SurfaceEditPanelContent surfaceInfo={surfaceInfo} groupInfo={groupInfo} />
			</div>
		</>
	)
})

interface SurfaceEnabledToggleProps {
	surfaceId: string
	enabled: boolean
	canChangeEnabled: boolean
}

/**
 * Toggle for enabling/disabling a surface.
 */
function SurfaceEnabledToggle({ surfaceId, enabled, canChangeEnabled }: SurfaceEnabledToggleProps) {
	const setEnabledMutation = useMutationExt(trpc.surfaces.surfaceSetEnabled.mutationOptions())

	const handleToggle = useCallback(
		(value: boolean) => {
			if (!canChangeEnabled) return
			setEnabledMutation.mutateAsync({ surfaceId, enabled: value }).catch((err) => {
				console.error('Failed to set surface enabled', err)
			})
		},
		[setEnabledMutation, surfaceId, canChangeEnabled]
	)

	const inputId = useId()

	return (
		<>
			<FormLabel htmlFor={inputId} className="col-sm-4 col-form-label col-form-label-sm">
				Enabled
				<InlineHelpIcon className="ms-1">When disabled, Companion will not open this surface.</InlineHelpIcon>
			</FormLabel>
			<Grid.Col sm={8}>
				<div className="mx-2">
					<SwitchInputField id={inputId} value={enabled} setValue={handleToggle} disabled={!canChangeEnabled} />
				</div>
				<div className="form-text">Only surfaces connected locally can be disabled here</div>
			</Grid.Col>
		</>
	)
}

interface SurfaceEditPanelContentProps {
	surfaceInfo: SurfaceInfo | null
	groupInfo: ClientDevicesListItem | null
}

function useSurfaceConfig(surfaceId: string | null) {
	const [surfaceConfig, setSurfaceConfig] = useState<SurfacePanelConfig | null>(null)
	const [configLoadError, setConfigLoadError] = useState<string | null>(null)

	const configSub = useSubscription(
		trpc.surfaces.watchSurfaceConfig.subscriptionOptions(
			{
				surfaceId: surfaceId ?? '',
			},
			{
				enabled: !!surfaceId,
				onStarted: () => {
					setConfigLoadError(null)
					setSurfaceConfig(null)
				},
				onData: (data) => {
					setSurfaceConfig(data)
				},
				onError: (err) => {
					console.error('Failed to load surface config', err)
				},
			}
		)
	)

	useEffect(() => setSurfaceConfig(null), [surfaceId])

	return {
		reset: configSub.reset,
		error: configLoadError,
		config: surfaceConfig,
	}
}
function useGroupConfig(groupId: string | null) {
	const [groupConfig, setGroupConfig] = useState<SurfaceGroupConfig | null>(null)
	const [configLoadError, setConfigLoadError] = useState<string | null>(null)

	const configSub = useSubscription(
		trpc.surfaces.watchGroupConfig.subscriptionOptions(
			{
				groupId: groupId ?? '',
			},
			{
				enabled: !!groupId,
				onStarted: () => {
					setConfigLoadError(null)
					setGroupConfig(null)
				},
				onData: (data) => {
					setGroupConfig(data)
				},
				onError: (err) => {
					console.error('Failed to load group config', err)
					setConfigLoadError(`Failed to load group config: ${err.message}`)
				},
			}
		)
	)

	useEffect(() => setGroupConfig(null), [groupId])

	return {
		reset: configSub.reset,
		error: configLoadError,
		config: groupConfig,
	}
}

const SurfaceEditPanelContent = observer<SurfaceEditPanelContentProps>(function SurfaceEditPanelContent({
	surfaceInfo,
	groupInfo,
}) {
	const { surfaces } = useContext(RootAppStoreContext)

	const surfaceId = surfaceInfo?.id ?? null
	const groupId = groupInfo?.id ?? null

	const surfaceConfig = useSurfaceConfig(surfaceId)
	const groupConfig = useGroupConfig(groupId)

	const surfaceConfigReset = surfaceConfig.reset
	const groupConfigReset = groupConfig.reset
	const doRetryConfigLoad = useCallback(() => {
		surfaceConfigReset()
		groupConfigReset()
	}, [surfaceConfigReset, groupConfigReset])

	// Remove unused onlineSurfaceIds for now - could be used for validation later
	// const onlineSurfaceIds = useComputed(() => {
	// 	const onlineSurfaceIds = new Set()
	// 	for (const group of surfaces.store.values()) {
	// 		if (!group) continue
	// 		for (const surface of group.surfaces) {
	// 			if (surface.isConnected) {
	// 				onlineSurfaceIds.add(surface.id)
	// 			}
	// 		}
	// 	}
	// 	return onlineSurfaceIds
	// }, [surfaces])

	const surfaceSetConfigKeyMutation = useMutationExt(trpc.surfaces.surfaceSetConfigKey.mutationOptions())
	const setSurfaceConfigValue = useCallback(
		(key: string, value: JsonValue | undefined) => {
			console.log('update surface', key, value)
			if (surfaceId) {
				surfaceSetConfigKeyMutation
					.mutateAsync({
						surfaceId,
						key,
						value,
					})
					.then((error) => {
						if (error) {
							console.log('Config update failed', error)
						}
					})
					.catch((e) => {
						console.log('Config update failed', e)
					})
			}
		},
		[surfaceSetConfigKeyMutation, surfaceId]
	)

	const setGroupConfigKeyMutation = useMutationExt(trpc.surfaces.groupSetConfigKey.mutationOptions())
	const setGroupConfigValue = useCallback(
		(key: string, value: JsonValue) => {
			console.log('update group', key, value)
			if (groupId) {
				setGroupConfigKeyMutation
					.mutateAsync({
						groupId,
						key,
						value,
					})
					.then((error) => {
						if (error) {
							console.log('group config update failed', error)
						}
					})
					.catch((e) => {
						console.log('group config update failed', e)
					})
			}
		},
		[setGroupConfigKeyMutation, groupId]
	)

	const surfaceSetGroupMutation = useMutationExt(trpc.surfaces.surfaceSetGroup.mutationOptions())
	const setSurfaceGroupId = useCallback(
		(groupId0: string) => {
			if (!surfaceId) return
			const groupId = !groupId0 || groupId0 === 'null' ? null : groupId0
			surfaceSetGroupMutation.mutateAsync({ groupId, surfaceId }).catch((e) => {
				console.log('Config update failed', e)
			})
		},
		[surfaceSetGroupMutation, surfaceId]
	)

	const setNameMutation = useMutationExt(trpc.surfaces.surfaceOrGroupSetName.mutationOptions())
	const updateName = useCallback(
		(surfaceOrGroupId: string, name: string) => {
			setNameMutation.mutateAsync({ surfaceOrGroupId, name }).catch((err) => {
				console.error('Update name failed', err)
			})
		},
		[setNameMutation]
	)

	const surfaceGroupChoices = useComputed(() => {
		const choices: DropdownChoice[] = [
			{ id: 'null', label: 'Standalone (Default)' },
			...surfaces.store
				.values()
				.filter((group): group is ClientDevicesListItem => !!group && !group.isAutoGroup)
				.map((group) => ({
					id: group.id,
					label: group.displayName,
				})),
		]
		return choices
	}, [surfaces])

	const nameFieldId = useId()
	const groupFieldId = useId()
	const groupNameFieldId = useId()
	const useLastPageFieldId = useId()
	const startupPageFieldId = useId()
	const currentPageFieldId = useId()
	const restrictPagesFieldId = useId()
	const allowedPagesFieldId = useId()
	const neverLockFieldId = useId()

	// Show loading or error state
	const dataReady = (!surfaceId || !!surfaceConfig.config) && (!groupId || groupConfig.config !== null)
	if (!dataReady || surfaceConfig.error || groupConfig.error) {
		return (
			<>
				<LoadingRetryOrError
					error={surfaceConfig.error || groupConfig.error}
					dataReady={dataReady}
					doRetry={doRetryConfigLoad}
					design="pulse"
				/>
			</>
		)
	}

	return (
		<>
			<Form className="row g-sm-2" onSubmit={PreventDefaultHandler}>
				{surfaceInfo && (
					<>
						<FormLabel htmlFor={nameFieldId} className="col-sm-4 col-form-label col-form-label-sm">
							Surface Name
						</FormLabel>
						<Grid.Col sm={8}>
							<TextInputFieldSimple
								id={nameFieldId}
								value={surfaceInfo.name}
								setValue={(name) => updateName(surfaceInfo.id, name)}
							/>
						</Grid.Col>

						{/* Show enabled toggle for all surfaces, but disable it for non-local ones */}
						{surfaceInfo.integrationType !== 'emulator' && surfaceInfo.integrationType !== 'elgato-plugin' && (
							<SurfaceEnabledToggle
								surfaceId={surfaceInfo.id}
								enabled={surfaceInfo.enabled}
								canChangeEnabled={surfaceInfo.canChangeEnabled}
							/>
						)}

						<FormLabel htmlFor={groupFieldId} className="col-sm-4 col-form-label col-form-label-sm">
							Surface Group
							<InlineHelpIcon className="ms-1">
								When in a group, surfaces will follow the page number of that group
							</InlineHelpIcon>
						</FormLabel>
						<Grid.Col sm={8}>
							<SimpleDropdownInputField
								id={groupFieldId}
								value={surfaceInfo.groupId || 'null'}
								setValue={(value) => setSurfaceGroupId(value as string)}
								choices={surfaceGroupChoices}
							/>
						</Grid.Col>
					</>
				)}

				{groupConfig.config && groupInfo && (
					<>
						{!groupInfo.isAutoGroup && (
							<>
								<FormLabel htmlFor={groupNameFieldId} className="col-sm-4 col-form-label col-form-label-sm">
									Group Name
								</FormLabel>
								<Grid.Col sm={8}>
									<TextInputFieldSimple
										id={groupNameFieldId}
										value={groupInfo.displayName}
										setValue={(name) => updateName(groupInfo.id, name)}
									/>
								</Grid.Col>
							</>
						)}

						<FormLabel htmlFor={useLastPageFieldId} className="col-sm-4 col-form-label col-form-label-sm">
							Use Last Page At Startup
						</FormLabel>
						<Grid.Col sm={8}>
							<div className="mx-2">
								<SwitchInputField
									id={useLastPageFieldId}
									value={!!groupConfig.config.use_last_page}
									setValue={(value) => setGroupConfigValue('use_last_page', !!value)}
								/>
							</div>
						</Grid.Col>

						<FormLabel htmlFor={startupPageFieldId} className="col-sm-4 col-form-label col-form-label-sm">
							{groupConfig.config.use_last_page ? 'Home Page' : 'Startup Page'}
						</FormLabel>
						<Grid.Col sm={8}>
							<InternalPageIdDropdown
								id={startupPageFieldId}
								disabled={false}
								includeDirection={false}
								includeStartup={false}
								value={groupConfig.config.startup_page_id}
								setValue={(val) => setGroupConfigValue('startup_page_id', val)}
							/>
						</Grid.Col>

						{(surfaceInfo === null || !!surfaceInfo.isConnected || !!groupConfig.config.use_last_page) && (
							<>
								<FormLabel htmlFor={currentPageFieldId} className="col-sm-4 col-form-label col-form-label-sm">
									{surfaceInfo === null || surfaceInfo?.isConnected ? 'Current Page' : 'Last Page'}
								</FormLabel>
								<Grid.Col sm={8}>
									<InternalPageIdDropdown
										id={currentPageFieldId}
										disabled={false}
										includeDirection={false}
										includeStartup={false}
										value={groupConfig.config.last_page_id}
										setValue={(val) => setGroupConfigValue('last_page_id', val)}
									/>
								</Grid.Col>
							</>
						)}

						<FormLabel htmlFor={restrictPagesFieldId} className="col-sm-4 col-form-label col-form-label-sm">
							Restrict pages accessible to this {surfaceId === null ? 'group' : 'surface'}
						</FormLabel>
						<Grid.Col sm={8}>
							<div className="mx-2">
								<SwitchInputField
									id={restrictPagesFieldId}
									value={!!groupConfig.config.restrict_pages}
									setValue={(value) => setGroupConfigValue('restrict_pages', !!value)}
								/>
							</div>
						</Grid.Col>

						{!!groupConfig.config.restrict_pages && (
							<>
								<FormLabel htmlFor={allowedPagesFieldId} className="col-sm-4 col-form-label col-form-label-sm">
									Allowed pages:
								</FormLabel>
								<Grid.Col sm={8}>
									<InternalPageIdDropdown
										id={allowedPagesFieldId}
										disabled={false}
										includeDirection={false}
										includeStartup={false}
										multiple={true}
										value={groupConfig.config.allowed_page_ids}
										setValue={(val) => setGroupConfigValue('allowed_page_ids', val)}
									/>
								</Grid.Col>
							</>
						)}

						<FormLabel htmlFor={neverLockFieldId} className="col-sm-4 col-form-label col-form-label-sm">
							Never pin code lock
						</FormLabel>
						<Grid.Col sm={8}>
							<div className="mx-2">
								<SwitchInputField
									id={neverLockFieldId}
									value={!!groupConfig.config.never_lock}
									setValue={(value) => setGroupConfigValue('never_lock', !!value)}
								/>
							</div>
						</Grid.Col>
					</>
				)}

				{surfaceConfig.config &&
					surfaceInfo &&
					surfaceInfo.configFields.map((field) => (
						<EditPanelConfigField
							key={field.id}
							definition={field}
							value={surfaceConfig.config?.[field.id]}
							setValue={setSurfaceConfigValue}
							isVisible
						/>
					))}

				{surfaceConfig.config && surfaceInfo && !surfaceInfo.isConnected && (
					<Grid.Col sm={12}>
						<NonIdealState
							icon={faQuestionCircle}
							text="This surface is currently offline. Some settings are not available."
						/>
					</Grid.Col>
				)}
			</Form>
		</>
	)
})
